import { useState, useEffect } from 'react';
import * as lookupApi from '../api/lookups';
import { Lookup, LookupType } from '../types/lookup.types';
import toast from 'react-hot-toast';

interface UseLookupResult {
  lookups: Record<LookupType, Lookup[]>;
  loading: boolean;
  error: string | null;
  getLookupsByType: (type: LookupType) => Lookup[];
  getLookupValue: (type: LookupType, id: string) => string;
  refreshLookups: () => Promise<void>;
}

export const useLookups = (): UseLookupResult => {
  const [lookups, setLookups] = useState<Record<LookupType, Lookup[]>>({} as Record<LookupType, Lookup[]>);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLookups = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await lookupApi.getAllLookups();
      
      // Convert array of lookups to record by type
      const lookupsByType = response.reduce((acc, lookup) => {
        if (!acc[lookup.type as LookupType]) {
          acc[lookup.type as LookupType] = [];
        }
        acc[lookup.type as LookupType].push(lookup);
        return acc;
      }, {} as Record<LookupType, Lookup[]>);

      // Sort lookups by order if available, otherwise by value
      Object.keys(lookupsByType).forEach(type => {
        lookupsByType[type as LookupType].sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return a.value.localeCompare(b.value);
        });
      });

      setLookups(lookupsByType);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch lookups';
      setError(message);
      toast.error('Failed to load lookups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLookups();
  }, []);

  const getLookupsByType = (type: LookupType): Lookup[] => {
    return lookups[type]?.filter(lookup => lookup.active) || [];
  };

  const getLookupValue = (type: LookupType, id: string): string => {
    const lookup = lookups[type]?.find(l => l._id === id);
    return lookup?.value || '';
  };

  return {
    lookups,
    loading,
    error,
    getLookupsByType,
    getLookupValue,
    refreshLookups: fetchLookups
  };
};
